import { AfterViewInit, ChangeDetectorRef, Component, DOCUMENT, ElementRef, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { CountUp, CountUpOptions } from 'countup.js';


interface Feature {
  icon: string;
  label: string;
  headline: string;
  tag: string;
  description: string;
  bullets: string[];
}

@Component({
  selector: 'app-landing-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss']
})
export class LandingPageComponent implements OnInit, AfterViewInit, OnDestroy {
  scrolled = false;
  menuOpen = false;
  activeFeature = 0;
  private observer?: IntersectionObserver;
  private statsAnimated = false;
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly titleService = inject(Title);
  private readonly metaService = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly canonicalUrl = 'https://app.fflxcel.com/landing-page';

  readonly trust = [
    ['◆', 'ATF Form 4473', 'Fully Compliant'],
    ['⌾', '256-bit SSL', 'Bank-grade Security'],
    ['★', 'SOC 2 Type II', 'Certified'],
    ['✓', 'BBB Accredited', 'A+ Rating'],
    ['✦', 'NSSF Member', 'Industry Partner'],
    ['◉', 'NICS Integrated', 'Instant Checks']
  ];

  readonly features: Feature[] = [
    { icon: '▤', label: 'ATF Automation', headline: 'The Deepest 4473 Engine Available', tag: 'No competitor does this', description: 'Auto-detects multi-handgun sales to trigger Form 3310.4. Auto-appends continuation sheets past 4 firearms. Versioned PDF templates ensure old records always render correctly, while reserved firearms and draft 4473s resume seamlessly.', bullets: ['Auto Form 3310.4 on multi-handgun sales', 'Continuation sheets — no paper overflow', 'Versioned 4473 PDF templates', 'Reserved firearm + draft 4473 workflow', 'Daily FFL lookup & license verification'] },
    { icon: '◫', label: 'Live Distributor Feeds', headline: 'All Your Distributors in One Screen', tag: 'No portal hopping', description: "Real-time quantity and pricing from RSR, Lipsey’s, CSSI, Davidson’s, and more in one unified view. One search, every source, live pricing.", bullets: ['RSR Group live feed', 'Lipsey’s real-time inventory', 'CSSI & Davidson’s integrated', 'Side-by-side price comparison', 'One-click purchase order creation'] },
    { icon: '$', label: 'Built-in Accounting', headline: 'No QuickBooks Required. Ever.', tag: 'Saves $100+/mo vs QuickBooks', description: 'A full accounting suite with chart of accounts, sales receipts, vendor credits, expense records, FIFO COGS, P&L, and balance-sheet reporting built directly into FFLXCEL.', bullets: ['Configurable chart of accounts', 'FIFO COGS per firearm lot', 'Accounts payable and vendor credits', 'Real-time P&L and balance sheet', 'Sales tax by jurisdiction'] },
    { icon: '▣', label: 'Kiosk + DL Scan', headline: 'Front Counter Speed Mode', tag: '3-second buyer intake', description: 'Barcode-scan a driver’s license to fill customer and 4473 buyer information in under three seconds. Deploy at the counter or let customers self-check in.', bullets: ['Barcode DL scan → auto-fill 4473', 'Kiosk self-service mode', 'State ID + passport support', 'Photo ID capture + storage', 'USB and tablet camera support'] },
    { icon: '↗', label: 'Purchase Optimizer', headline: 'Always Buy at the Lowest Cost', tag: 'Unique to FFLXCEL', description: 'Cross-reference live vendor costs to find the cheapest source for each item, then compare competitor retail listings to protect margin before you order.', bullets: ['Cheapest vendor recommendation per SKU', 'Competitor price scraping', 'Margin calculator at reorder time', 'Reorder point automation', 'Invoice PDF SKU/qty/cost extraction'] },
    { icon: '☁', label: 'Offline + Sync', headline: 'Works Without the Internet', tag: 'Works in dead zones', description: 'An offline-first PWA keeps work moving through outages. Queued writes sync on reconnect while live updates propagate across tabs, devices, and locations.', bullets: ['Keep working without wifi', 'Write queue syncs on reconnect', 'Real-time multi-device sync', 'Multi-location scale', 'Installable PWA on any device'] }
  ];

  readonly comparisons = [
    ['Native accounting — no QuickBooks needed', false], ['Live multi-distributor feeds', false], ['Purchase optimizer + competitor price scraping', false], ['Auto Form 3310.4 on multi-handgun sales', false], ['Reserved firearm + draft 4473 workflow', false], ['Kiosk mode with barcode DL scanning', false], ['Offline-first PWA', false], ['Real-time multi-tab sync', false], ['Invoice PDF parser', false], ['Daily FFL lookup & license verification', false], ['Standard Form 4473 digitization', true], ['Basic inventory tracking', true]
  ];

  readonly testimonials = [
    { quote: 'We switched from paper-based 4473s and I’ll never go back. FFLXCEL cut our transfer time from 20 minutes down to under 4. ATF came in for a compliance review and said our records were the cleanest they’d seen in 15 years.', name: 'Marcus D.', title: 'Owner, Lone Star Arms — Austin, TX', stat: '4 min', label: 'avg. transfer time' },
    { quote: 'The inventory module alone is worth the subscription. We have three locations and before FFLXCEL we had no idea what was in each store without calling. Now every serial number is visible in real time.', name: 'Sandra K.', title: 'Co-Owner, Keystone Firearms — Lancaster, PA', stat: '3 stores', label: 'managed in one view' },
    { quote: 'The compliance score dashboard gave us peace of mind I didn’t know I was missing. FFLXCEL flagged gaps in our bound book before an audit could catch us. Worth it ten times over.', name: 'Ray T.', title: 'FFL Type 01/SOT Dealer — Nashville, TN', stat: '100%', label: 'compliance score' }
  ];

  readonly plans = [
    { name: 'Basic', price: '$9.99', suffix: '/month', desc: 'Perfect for single-location dealers getting started.', popular: false, features: ['Up to 500 annual transfers', 'Form 4473 Quick Fill & Bound Book Integration', 'Basic reporting dashboard', 'Email support', '2 staff accounts'] },
    { name: 'Professional', price: '$29.99', suffix: '/month', desc: 'The complete solution for growing FFL businesses.', popular: true, features: ['Unlimited Annual Transfers', 'Form 4473 Quick Fill & Bound Book Integration', 'Full Accounting Suite Including Invoice Parser', 'Live Distributor Inventory Feeds', 'AI-Powered Analytics and Suggestions', 'Optimize Purchase Orders For Lowest Cost', 'Priority 24/7 Support'] },
    // { name: 'Enterprise', price: 'Custom', suffix: '', desc: 'For high-volume and multi-store chains.', popular: false, features: ['Everything in Professional', 'Unlimited staff accounts', 'Dedicated account manager', 'Custom API integrations', 'White-glove onboarding', 'SLA uptime guarantee', 'Custom reporting', 'On-site training available'] }
  ];

  ngOnInit(): void {
    const title = 'FFL Software for 4473, Compliance & Inventory | FFLXCEL';
    const description = 'FFLXCEL helps licensed firearm dealers automate ATF Form 4473, manage bound books and inventory, compare distributor pricing, and run accounting in one platform.';
    const imageUrl = 'https://app.fflxcel.com/assets/images/login.webp';

    this.titleService.setTitle(title);
    [
      { name: 'description', content: description },
      { name: 'robots', content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'FFLXCEL' },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: this.canonicalUrl },
      { property: 'og:image', content: imageUrl },
      { property: 'og:image:alt', content: 'FFLXCEL firearm dealer management software' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: imageUrl },
    ].forEach(tag => this.metaService.updateTag(tag));

    this.setCanonicalUrl();
    this.addStructuredData();
  }

  private setCanonicalUrl(): void {
    let canonical = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = this.document.createElement('link');
      canonical.rel = 'canonical';
      this.document.head.appendChild(canonical);
    }
    canonical.href = this.canonicalUrl;
  }

  private addStructuredData(): void {
    this.document.getElementById('fflxcel-structured-data')?.remove();
    const script = this.document.createElement('script');
    script.id = 'fflxcel-structured-data';
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'FFLXCEL',
      url: this.canonicalUrl,
      description: 'All-in-one firearm dealer software for ATF Form 4473 automation, bound-book compliance, inventory, distributor feeds, and accounting.',
      applicationCategory: 'BusinessApplication',
      applicationSubCategory: 'Firearm dealer management software',
      operatingSystem: 'Web browser',
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'USD',
        lowPrice: '30',
        offerCount: '3',
      },
      featureList: [
        'ATF Form 4473 automation',
        'Electronic bound book management',
        'Live distributor inventory feeds',
        'Built-in accounting',
        'Driver license scanning and kiosk mode',
        'Offline-first synchronization',
      ],
    });
    this.document.head.appendChild(script);
  }
  @HostListener('window:scroll') onScroll(): void {
    this.scrolled = window.scrollY > 20;
  }

  scrollTo(id: string): void {
    this.menuOpen = false;
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  toggleMenu(): void { this.menuOpen = !this.menuOpen; }

  selectPlan(plan: { name: string; price: string }): void {
    sessionStorage.setItem('plan_selected', '1');
    this.router.navigate(['/landing-page-sign-up'], {
      queryParams: { plan: plan.name, price: plan.price }
    });
  }



  contactSales(): void {
    window.location.href = 'mailto:sales@fflxcel.net?subject=FFLXCEL%20Custom%20Plan';
  }

  private animateStats(): void {
    if (this.statsAnimated) return;
    this.statsAnimated = true;

    const counters: Array<[string, number, CountUpOptions]> = [
      ['dealers', 500, { suffix: '+', duration: 2.4 }],
      ['transactions', 2, { suffix: 'M+', duration: 2.4 }],
      ['uptime', 99.9, { decimalPlaces: 1, suffix: '%', duration: 2.4 }],
      ['support', 24, { suffix: '/7', duration: 2.4 }],
    ];

    counters.forEach(([name, endValue, options]) => {
      const element = this.host.nativeElement.querySelector<HTMLElement>(`[data-counter="${name}"]`);
      if (!element) return;

      const counter = new CountUp(element, endValue, {
        useEasing: true,
        smartEasingThreshold: 999,
        ...options,
      });

      if (!counter.error) counter.start();
    });
  }

  ngAfterViewInit(): void {
    this.onScroll();
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('.reveal').forEach(el => el.classList.add('visible'));
      this.animateStats();
      return;
    }
    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          if (entry.target.classList.contains('stat-grid')) this.animateStats();
        }
      });
    }, { threshold: 0.12 });
    document.querySelectorAll('.reveal').forEach(el => this.observer?.observe(el));
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
